import type { Step } from "react-joyride";
import { ACTIONS } from "react-joyride";

import { MAIN_SIDEBAR_SCROLL_SELECTOR } from "@/constants/tour";

import type {
  IPollForTargetAndResumeProps,
  IScrollStepTargetIntoViewProps,
  ITourStepConfig,
} from "./types";

export const getStepDirection = (action: string): number | undefined => {
  if (action === ACTIONS.NEXT) return 1;
  if (action === ACTIONS.PREV) return -1;
  return undefined;
};

export const createTourStep = ({
  target,
  title,
  content,
  placement,
  floaterProps,
}: ITourStepConfig): Step => ({
  target,
  title,
  content,
  disableBeacon: true,
  placement,
  ...(floaterProps !== undefined && { floaterProps }),
});

export const createTourStepBuilder =
  (defaultPlacement?: Step["placement"]) =>
  (config: ITourStepConfig): Step =>
    createTourStep({
      ...config,
      placement: config.placement ?? defaultPlacement,
    });

export const scrollStepTargetIntoView = ({
  steps,
  stepIndex,
}: IScrollStepTargetIntoViewProps): void => {
  const targetCandidate = steps[stepIndex]?.target;
  const target =
    typeof targetCandidate === "string" ? targetCandidate : undefined;

  if (!target) return;

  const element = document.querySelector(target);

  if (element instanceof HTMLElement) {
    element.scrollIntoView({ block: "nearest", behavior: "auto" });
  }
};

export const scrollMainSidebarToTop = (): void => {
  const viewport = document.querySelector<HTMLElement>(
    MAIN_SIDEBAR_SCROLL_SELECTOR,
  );
  if (!viewport) return;
  viewport.scrollTop = 0;
};

const STABLE_FRAMES_REQUIRED = 3;

// NOTE: Waits for the target element's position to stabilize across consecutive
// animation frames before invoking the callback. This prevents react-floater from
// capturing a stale position during layout shifts (e.g. scrollbar appearing/disappearing
// causing flex-wrap reflow).
export const waitForStableTarget = (
  selector: string,
  onStable: () => void,
): void => {
  const element = document.querySelector(selector);

  if (!element) {
    onStable();
    return;
  }

  let lastTop = -1;
  let lastLeft = -1;
  let stableFrames = 0;

  const checkStability = (): void => {
    const { top, left } = element.getBoundingClientRect();

    if (top === lastTop && left === lastLeft) {
      stableFrames++;
    } else {
      stableFrames = 0;
    }

    lastTop = top;
    lastLeft = left;

    if (stableFrames >= STABLE_FRAMES_REQUIRED) {
      onStable();
    } else {
      requestAnimationFrame(checkStability);
    }
  };

  requestAnimationFrame(checkStability);
};

export const pollForTargetAndResume = ({
  selectorToPoll,
  deadline,
  stepTarget,
  navigationTimeoutRef,
  onResumed,
}: IPollForTargetAndResumeProps): void => {
  if (selectorToPoll && !document.querySelector(selectorToPoll)) {
    if (Date.now() < deadline) {
      navigationTimeoutRef.current = setTimeout(
        () =>
          pollForTargetAndResume({
            selectorToPoll,
            deadline,
            stepTarget,
            navigationTimeoutRef,
            onResumed,
          }),
        50,
      );
      return;
    }
  }

  // NOTE: Wait for the target's position to stabilize before resuming.
  // After page navigation, layout shifts (e.g. scrollbar appearing then
  // disappearing) can cause flex-wrapped elements to reflow. Resuming
  // too early makes react-floater capture a stale position.
  waitForStableTarget(stepTarget, onResumed);
};
